import {AnyAttrs, AttrsSchema, Component} from 'panel';
import cuid from 'cuid';

import {
  ESCAPE_KEY_CODE,
  eventWasOutside,
  offAnimationEnd,
  onAnimationEnd,
  rafRetry,
  TAB_KEY_CODE,
} from 'common/util/dom';
import {getActiveElement, getFocusableChildren} from 'common/util/focusable';

export enum Visibility {
  Closed = `closed`,
  Closing = `closing`,
  Open = `open`,
  Opening = `opening`,
}

export const VisibilityEvents: {[x in Visibility]: string} = {
  [Visibility.Closed]: `stackable-closed`,
  [Visibility.Closing]: `stackable-closing`,
  [Visibility.Open]: `stackable-open`,
  [Visibility.Opening]: `stackable-opening`,
};

interface BaseState {
  visibility: Visibility;
}

interface StackableComponentAttrs {
  'prevent-close-selectors': string[];
}

// elements that increase the stacking context

const STACKABLE_ELEMENTS = [
  `mp-confirm`,
  `mp-create-entity-modal`,
  `mp-custom-event-modal-internal`,
  `mp-sharing-modal`,
  `mp-custom-property-modal`,
  `mp-drop-menu`,
  `mp-inline-cohorts-modal`,
  `mp-modal`,
];

const STACKABLE_ELEMENTS_SELECTOR = STACKABLE_ELEMENTS.join(`,`);

export const ANIMATION_TARGET_REF = `contentBody`;

export abstract class StackableComponent<T = UnknownObj, Attrs = AnyAttrs> extends Component<
  BaseState & T,
  Attrs & StackableComponentAttrs
> {
  static contextStack = new Set<string>();
  private stackingContext: number;
  public stackId: string;

  static get attrsSchema(): AttrsSchema<AnyAttrs> {
    return {
      'prevent-close-selectors': {
        description: `selectors that prevent closing the current stackable element e.g ['#help-button', '.widget-hover-fill']`,
        type: `json`,
        default: [],
      },
    };
  }

  /**
   * Sets the current stacking context target to a given element id
   */
  static setCurrentStackingContextTarget(id: string) {
    StackableComponent.contextStack.add(id);
  }

  /**
   * Removes the stacking context target from a given element id
   */
  static deleteCurrentStackingContextTarget(id: string) {
    StackableComponent.contextStack.delete(id);
  }

  /**
   * Checks if a given element id is the current stacking context target
   */
  static isCurrentStackingContextTarget(id: string) {
    return Array.from(StackableComponent.contextStack).pop() === id;
  }

  get config() {
    return {
      ...super.config,
      defaultState: {
        ...super.config.defaultState,
        visibility: Visibility.Closed,
      },
    };
  }

  get isOpen() {
    return [Visibility.Open, Visibility.Opening].includes(this.state.visibility);
  }

  get trapTab() {
    return true;
  }

  get shouldCloseWhenClickedOutside() {
    return true;
  }

  get shouldCloseOnEsc() {
    return true;
  }

  constructor() {
    super();

    this.stackId = cuid();
    this.handleWindowKeydown = this.handleWindowKeydown.bind(this);
    this.onAnimationEnd = this.onAnimationEnd.bind(this);
    this.onStackableOpening = this.onStackableOpening.bind(this);
    this.mousedownOutsideListener = this.mousedownOutsideListener.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    onAnimationEnd(this.el, this.onAnimationEnd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    offAnimationEnd(this.el, this.onAnimationEnd);
    this.onClose();
  }

  protected onOpening() {
    this.focusFirstChildren();
  }

  protected onOpen() {
    this.focusFirstChildren();
    this.addKeyListeners();
  }

  protected onClose() {
    this.removeKeyListeners();
    StackableComponent.deleteCurrentStackingContextTarget(this.stackId);
    window.removeEventListener(VisibilityEvents[Visibility.Opening], this.onStackableOpening);
  }

  private dispatchChange() {
    this.dispatchEvent(new CustomEvent(`change`, {detail: {state: this.state.visibility}}));
  }

  protected setVisibility(visibility: Visibility) {
    this.update({visibility} as BaseState & T);
    this.dispatchEvent(
      new CustomEvent(VisibilityEvents[visibility], {
        bubbles: true,
        composed: true,
        detail: {target: this.stackId},
      }),
    );

    switch (visibility) {
      case Visibility.Open:
        this.dispatchChange();
        this.onOpen();
        break;
      case Visibility.Opening:
        this.onOpening();
        break;
      case Visibility.Closed:
        this.dispatchChange();
        this.onClose();
        break;
    }
  }

  public isCurrentStackableTarget() {
    return StackableComponent.isCurrentStackingContextTarget(this.stackId);
  }

  /**
   * Opens the stackable element the following these transitions:
   * closed -> opening
   * closing -> open
   */
  public open() {
    switch (this.state.visibility) {
      case Visibility.Open:
      case Visibility.Opening:
        break;
      case Visibility.Closing:
        this.setVisibility(Visibility.Open);
        break;
      case Visibility.Closed:
        window.addEventListener(VisibilityEvents[Visibility.Opening], this.onStackableOpening);
        this.setVisibility(Visibility.Opening);
        break;
    }
  }

  /**
   * The underlying content of the stackable element is expected
   * to use a css animation to bring it in or out of view, once
   * that animation completes, these transitions are followed:
   * opening -> opened
   * closing -> closed
   */
  private onAnimationEnd(ev: AnimationEvent) {
    const isTarget = (ev.target as HTMLElement).getAttribute(`elref`) === ANIMATION_TARGET_REF;

    if (isTarget) {
      switch (this.state.visibility) {
        case Visibility.Opening:
          this.setVisibility(Visibility.Open);
          break;
        case Visibility.Closing:
          this.setVisibility(Visibility.Closed);
          break;
      }
    }
  }

  /**
   * Closes the stackable element the following these transitions:
   * opening -> closed
   * open -> closing
   */
  public close() {
    switch (this.state.visibility) {
      case Visibility.Closed:
      case Visibility.Closing:
        break;
      case Visibility.Opening:
        this.setVisibility(Visibility.Closed);
        break;
      case Visibility.Open:
        this.setVisibility(Visibility.Closing);
        break;
    }
  }

  protected isCloseable() {
    return true;
  }

  private onStackableOpening(ev) {
    const {target} = ev.detail;
    const stackingContext = this.getStackingContextFromEvent(ev);

    if (target === this.stackId) {
      this.stackingContext = stackingContext;
      StackableComponent.setCurrentStackingContextTarget(this.stackId);
    } else if (this.isOpen && this.isCloseable() && stackingContext === this.stackingContext) {
      this.close();
    }
  }

  private getStackingContextFromEvent(ev) {
    const path = typeof ev.composedPath !== `function` ? ev.path : ev.composedPath();

    return path.reduce((context, el) => {
      return context + (el.matches && el.matches(STACKABLE_ELEMENTS_SELECTOR) ? 1 : 0);
    }, 0);
  }

  /**
   * Close current stackable element when clicked outside its target area, or when
   * clicking an element selector defined in attr(`prevent-close-selectors`)
   */
  private mousedownOutsideListener(ev: MouseEvent) {
    const clickWasOutside = eventWasOutside(ev, this, this.attr(`prevent-close-selectors`));

    if (this.isOpen && this.shouldCloseWhenClickedOutside && clickWasOutside && this.isCurrentStackableTarget()) {
      this.close();
    }
  }

  /**
   * Returns all focusable children in the light DOM tree or within the shadow root
   */
  protected getFocusableChildren() {
    return [...getFocusableChildren(this), ...(this.config.useShadowDom ? getFocusableChildren(this.el) : [])];
  }

  /**
   * Focus on first focusable child when the component is opened
   */
  private focusFirstChildren() {
    rafRetry(() => {
      const firstChild = this.getFocusableChildren().shift();
      if (firstChild) {
        firstChild.focus();
        return true;
      }
      return false;
    }).catch();
  }

  /**
   * Changes focus to next focusable child
   */
  private focusNextChildrenOnTab(ev: KeyboardEvent) {
    const children = this.getFocusableChildren();
    // If there are no focusable children, prevent tabbing through the rest
    // of the document before clearing focus on the dialog
    if (!children.length) {
      return ev.preventDefault();
    }

    const focusedItemIndex = children.indexOf(getActiveElement(document, children));

    if ((ev.shiftKey && focusedItemIndex === 0) || focusedItemIndex === -1) {
      children[children.length - 1].focus();
      ev.preventDefault();
    } else if (!ev.shiftKey && focusedItemIndex === children.length - 1) {
      children[0].focus();
      ev.preventDefault();
    }
  }

  /**
   * Closes the current stackable element on ESC and focus on next children on TAB
   */
  private handleWindowKeydown(ev: KeyboardEvent) {
    if (!this.isOpen || !this.isCurrentStackableTarget()) {
      return;
    }

    switch (ev.keyCode) {
      case ESCAPE_KEY_CODE:
        if (this.shouldCloseOnEsc) {
          this.close();
        }
        break;
      case TAB_KEY_CODE:
        if (this.trapTab) {
          this.focusNextChildrenOnTab(ev);
        }
        break;
    }
  }

  private addKeyListeners() {
    document.body.addEventListener(`mousedown`, this.mousedownOutsideListener, true);
    window.addEventListener(`keydown`, this.handleWindowKeydown, true);
  }

  private removeKeyListeners() {
    document.body.removeEventListener(`mousedown`, this.mousedownOutsideListener, true);
    window.removeEventListener(`keydown`, this.handleWindowKeydown, true);
  }
}
